import React, { useMemo } from 'react';
import { NamedLink, Avatar } from '../../components';
import { createSlug } from '../../util/urlHelpers';

import css from './CommunityPage.module.css';

/**
 * Extracts the URL of the first image found in HTML content
 *
 * @param {string} html - HTML content to parse
 * @returns {string|null} - URL of the first image or null if not found
 */
const extractFirstImageUrl = html => {
  if (!html || typeof document === 'undefined') {
    return null;
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const firstImg = tempDiv.querySelector('img');
  return firstImg ? firstImg.src : null;
};

/**
 * Component to display a featured post item
 *
 * @param {Object} props - Component props
 * @param {Object} props.post - Post data
 */
const FeaturedPostItem = ({ post }) => {
  const { title } = post.attributes;
  const { contentType, descriptionHtml } = post.attributes.publicData || {};
  const author = post.author;
  const { displayName } = author.attributes.profile;

  // Determine the image URL to use for the background
  const imageUrl = useMemo(() => {
    if (contentType === 'text' && descriptionHtml) {
      return extractFirstImageUrl(descriptionHtml);
    } else {
      const imageVariants = post.images?.[0]?.attributes?.variants;
      return imageVariants?.['portrait-crop2x']?.url || null;
    }
  }, [contentType, descriptionHtml, post.images]);

  // Create style object with background image if available
  const style = useMemo(() => {
    return imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined;
  }, [imageUrl]);

  return (
    <NamedLink
      className={css.featuredContentItem}
      name="PostPage"
      params={{ id: post.id.uuid, slug: createSlug(title) }}
      style={style}
    >
      <div className={css.featuredContentItemWrapper}>
        <h3 className={css.featuredContentItemTitle}>{title}</h3>
        <div className={css.featuredContentItemUser}>
          <Avatar className={css.featuredContentItemUserAvatar} user={author} disableProfileLink />
          <span className={css.featuredContentItemUserDisplayName}>{displayName}</span>
        </div>
      </div>
    </NamedLink>
  );
};

/**
 * Component that displays a section of featured posts
 * Renders nothing if there are no featured posts or if a category filter is active
 *
 * @param {Object} props - Component props
 * @param {Array} props.featuredPosts - List of featured posts to display
 * @param {Object} props.currentSearchParams - Current URL search parameters
 */
const SectionFeatured = ({ featuredPosts, currentSearchParams }) => {
  // Don't render if there are no posts or a category filter is applied
  const noResults = featuredPosts.length === 0;
  const searchCategory = currentSearchParams?.pub_category;

  if (noResults || searchCategory) {
    return null;
  }

  return (
    <div className={css.sectionFeatured}>
      <div className={css.featuredContent}>
        {featuredPosts.map(post => (
          <FeaturedPostItem key={post.id.uuid} post={post} />
        ))}
      </div>
    </div>
  );
};

export default SectionFeatured;
